import type { IStep, ISubStep } from 'components/VerticalStepper/VerticalStepper';
import { formatShortDateAndHour } from 'utils/formatDatesAndHours';
import { getEventContentByUuid, replaceEventPlaceholders } from 'utils/history-events';
import {
  IOrderStatusHistoryItem,
  ORDER_STATUS_LOG_NAMES,
  IShipmentStatusHistoryItem
} from 'types/order-status-log.types';
import { STEPS_STYLE_NAMES, STEPS_STYLES } from 'components/VerticalStepper/VerticalStepper.types';
import { LOCATION_PLACE_TYPE } from 'types/services';

const ORDER_STATUS_ID_CANCELLED = 3;

/**
 * Etiqueta de destino del envío para mostrar "Envío a X":
 * - usuario → nombre del receptor (ej. "Pepito Perez")
 * - nudos → "bodega"
 * - office → "oficina"
 */
export const getShipmentDestinationLabel = (shipment: {
  place?: string;
  receiverInfo?: { receiverName?: string };
  country?: { name?: string };
}) => {
  const place = shipment?.place;
  const receiverName = shipment?.receiverInfo?.receiverName ?? '';
  if (place === LOCATION_PLACE_TYPE.USER) return receiverName || 'Usuario';
  if (place === LOCATION_PLACE_TYPE.NUDOS) return 'bodega';
  if (place === LOCATION_PLACE_TYPE.OFFICE) return 'oficina';
  return receiverName || 'Envío';
};

const getStatusTitle = (item: IOrderStatusHistoryItem): string => {
  return ORDER_STATUS_LOG_NAMES[item.orderStatusId] ?? item.orderStatus?.name ?? `Estado ${item.orderStatusId}`;
};

/** Ventana de tiempo de un estado de orden: desde createdAt de este hasta createdAt del siguiente (o sin tope si es el último). */
const isShipmentLogInWindow = (
  shipmentLog: IShipmentStatusHistoryItem,
  orderFrom: string,
  orderTo: string | null
): boolean => {
  if (shipmentLog.createdAt < orderFrom) return false;
  if (orderTo == null) return true;
  return shipmentLog.createdAt < orderTo;
};

/**
 * Transforma el historial de la orden y de los envíos en pasos del VerticalStepper.
 * En cada estado de la orden se muestra: el cambio de estado (quién movió), los cambios de estado de los envíos en esa misma ventana de tiempo y los eventos asociados.
 * @param shipmentLabels - Mapa shipmentId → etiqueta de destino.
 */
export const transformOrderStatusLogHistoryToSteps = (
  history: IOrderStatusHistoryItem[],
  shipments: { shipmentId: number; history: IShipmentStatusHistoryItem[] }[],
  shipmentLabels?: Record<number, string>
): IStep[] => {
  if (!history?.length) return [];

  const lastIndex = history.length - 1;

  return history
    .map((item, index) => {
      const isLast = index === lastIndex;
      const isCancelled = item.orderStatusId === ORDER_STATUS_ID_CANCELLED;
      const nextOrderItem = history[index + 1] ?? null;
      const orderFrom = item.createdAt;
      const orderTo = nextOrderItem?.createdAt ?? null;

      let style = STEPS_STYLES[STEPS_STYLE_NAMES.SUCCESS];
      if (isLast && isCancelled) {
        style = STEPS_STYLES[STEPS_STYLE_NAMES.CANCELLED];
      } else if (isLast) {
        style = STEPS_STYLES[STEPS_STYLE_NAMES.IN_PROGRESS];
      }

      const dateText = formatShortDateAndHour(item.createdAt)?.textWithTimezone ?? '';
      const statusName = getStatusTitle(item);
      const whoMoved = item.responsibleUser
        ? `Movido por: ${item.responsibleUser.firstName} ${item.responsibleUser.lastName}`
        : null;
      const orderDescription = [`Paso a estado ${statusName}`, whoMoved].filter(Boolean).join('\n');

      const orderSubStep: ISubStep & { _sortDate: string } = {
        title: 'Cambio de estado de la orden',
        date: dateText,
        description: orderDescription,
        _sortDate: item.createdAt
      };

      const shipmentSubSteps: (ISubStep & { _sortDate: string })[] = [];
      for (const { shipmentId, history: shipmentHistory } of shipments) {
        const destinationLabel = shipmentLabels?.[shipmentId];
        const title = destinationLabel ? `Envío a ${destinationLabel}` : `Envío #${shipmentId}`;
        for (const log of shipmentHistory) {
          if (!isShipmentLogInWindow(log, orderFrom, orderTo)) continue;
          const logDate = formatShortDateAndHour(log.createdAt)?.textWithTimezone ?? '';
          const statusName = log.shipmentStatus?.name ?? 'Estado';
          shipmentSubSteps.push({
            title,
            date: logDate,
            description: `Paso a estado ${statusName}`,
            _sortDate: log.createdAt
          });
        }
      }

      const eventSubSteps: (ISubStep & { _sortDate: string })[] = (item.events ?? []).map(event => {
        const { createdAt, eventType, userId, relevantInfo } = event;

        const mappedContent = getEventContentByUuid(eventType?.uuid ?? '');
        const title = mappedContent.name ?? eventType?.name ?? 'Evento';
        const descriptionSource = mappedContent.description ?? eventType?.description ?? '';
        const description = replaceEventPlaceholders(descriptionSource, userId, relevantInfo);

        return {
          title,
          date: formatShortDateAndHour(createdAt)?.textWithTimezone ?? '',
          description,
          _sortDate: createdAt
        };
      });

      const allSubSteps = [orderSubStep, ...shipmentSubSteps, ...eventSubSteps];
      const subSteps: ISubStep[] = allSubSteps
        .sort((a, b) => (a._sortDate > b._sortDate ? -1 : 1))
        .map(({ _sortDate: _, ...s }) => s);

      return {
        title: getStatusTitle(item),
        titleColor: style.titleColor,
        icon: style.icon,
        isOpaque: style.isOpaque,
        subSteps
      };
    })
    .reverse();
};
